/**
 * events.ts
 *
 * API layer for Musicade events.
 *
 * Endpoints:
 *  GET /event/all_events/                         → all categorised event lists (list page)
 *  GET /profiles/events/{user_id}/                → profile events (paginated)
 *  GET /event/individual_event/{id}/{filter}/     → single event detail
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { nowAsWallClockMs } from '../utils/eventDate';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EventMedia {
  id: number;
  media_type: string;
  /** Primary display URL (Cloudinary / S3) */
  file_url?: string;
  image?: string;
  file?: string;
  video?: string;
}

export interface EventTicket {
  id?: number;
  price: number;
  ticket_type?: string;
  description?: string;
  remaining_tickets?: number;
  ticket_number_issued?: number;
  access_type?: string;
  start_at?: string;
  expired_at?: string;
  is_archived?: boolean;
  is_multi_event_pass?: boolean;
  accessible_sub_event_ids?: number[];
  set_purchase_date?: boolean;
  use_publish_time_for_start_at?: boolean;
}

export interface EventCollaborator {
  id: number;
  username: string;
  prof_pics?: string;
  full_name?: string;
  role?: string;
}

/** A project collaborator with edit access derived from the linked project's team */
export interface EventProjectCollaborator {
  role: string;
  user_id: number;
}

export interface EventCreatorProfile {
  id: number;
  username: string;
  full_name?: string;
  prof_pics?: string;
  profile_image?: string;
  user_types?: string[];
}

export interface EventGenre {
  id: number;
  name?: string;
  slug?: string;
}

export interface AttendeePreview {
  id: number;
  username: string;
  profile_image?: string | null;
}

export interface Attendance {
  going_count: number;
  total_spots: number;
  spots_remaining: number;
  preview_avatars: AttendeePreview[];
  friends_going_count: number | null;
  friends_going_preview: AttendeePreview[] | null;
  friends_going_others_count: number | null;
}

export interface AssociatedCreator {
  id: number;
  username: string;
  full_name?: string;
  profile_image?: string | null;
  role: string;
}

export interface EventHost extends EventCreatorProfile {
  bio?: string;
  events_hosted_count?: number;
  is_following?: boolean | null;
  associated_creators?: AssociatedCreator[];
}

export interface EventItem {
  id: number;
  event_title?: string;
  /** "single" | "multi_event" */
  event_structure_type?: string;
  event_type?: string | null;
  /** About / description text */
  info?: string;
  /** ISO 8601 start datetime */
  start?: string;
  /** ISO 8601 end datetime */
  end?: string;
  /** WKT point: "POINT (lng lat)" */
  location?: string;
  venue?: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  timezone?: string;
  /** Raw IANA zone key from the API (e.g. "America/New_York"); `timezone` above already prefers this when present. */
  iana_timezone?: string;
  /** Timezone exactly as the API returns it in `timezone` (display label, e.g. "GMT+1"). */
  timezone_label?: string;
  is_cancelled?: boolean;
  is_private?: boolean;
  is_sub_event?: boolean;
  /** "draft" | "published" */
  is_draft_or_published?: string;
  /** Numeric user ID of the creator */
  event_creator?: number;
  /** Explicit creator ID field (alias) */
  event_creator_id?: number;
  event_uuid?: string;
  /** External ticketing URL */
  external_ticketing_url?: string;
  /** "internal" | "external" */
  event_ticketing_type?: string;
  event_age?: string | null;
  genres?: EventGenre[];
  medias?: EventMedia[];
  tickets?: EventTicket[];
  /**
   * Draft tickets returned by the draft detail endpoint
   * (the published endpoint uses `tickets` instead). Kept on the
   * type so callers can introspect raw API payloads when needed,
   * but the canonical ticket list is always exposed via `tickets`.
   */
  tickets_draft?: EventTicket[];
  collaborators?: EventCollaborator[];
  /** Collaborators of the project this event is linked to, used for edit permission checks */
  project_collaborators?: EventProjectCollaborator[];
  /** Nested creator profile returned by the detail endpoint */
  event_creator_profile?: EventCreatorProfile;
  /** Alternative field name used by some endpoints */
  event_creator_details?: EventCreatorProfile;
  /** Rich host profile (bio, events hosted count, follow state, associated creators) */
  host?: EventHost;
  /** Real attendance/capacity data — the source of truth for the going/spots UI */
  attendance?: Attendance;
  created_at?: string;
  directions?: string;
  location_directions?: string;
  /** Featured image returned by the public profile endpoint (profile-embedded events) */
  featured_image?: {
    id?: number;
    media_type?: string;
    file_url?: string;
    thumbnail_url?: string;
  } | null;
  /** Whether the signed-in user has marked interest in this event */
  interested?: boolean;
  interested_count?: number;
  /** Dominant color of the first/primary event image, as a "#rrggbb" hex string. */
  dominant_color?: string | null;
}

/** Full response from GET /event/all_events/ */
export interface RefinedEventResponse {
  recommended_events: EventItem[];
  recent: EventItem[];
  my_created_events: EventItem[];
  collaborators: EventItem[];
  ticket_bought: EventItem[];
  cancelled: EventItem[];
  event_draft: EventItem[];
  draft_single_events: EventItem[];
  draft_multi_events: EventItem[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normaliseEvent(raw: any): EventItem {
  const loc = raw.location_body ?? {};
  return {
    id: raw.id,
    event_title: raw.event_title,
    event_structure_type: raw.event_structure_type,
    event_type: raw.event_type,
    info: raw.info,
    start: raw.start,
    end: raw.end,
    location: raw.location,
    venue: raw.venue ?? raw.address,
    address: raw.address ?? raw.venue,
    street: raw.street ?? loc.street,
    city: raw.city ?? loc.city,
    state: raw.state ?? loc.state,
    country: raw.country ?? loc.country,
    timezone: raw.iana_timezone ?? raw.timezone,
    iana_timezone: raw.iana_timezone,
    timezone_label: raw.timezone,
    is_cancelled: raw.is_cancelled,
    is_private: raw.is_private,
    is_sub_event: raw.is_sub_event,
    is_draft_or_published: raw.is_draft_or_published,
    event_creator: raw.event_creator ?? raw.event_creator_id,
    event_creator_id: raw.event_creator_id ?? raw.event_creator,
    event_uuid: raw.event_uuid,
    external_ticketing_url: raw.external_ticketing_url,
    event_ticketing_type: raw.event_ticketing_type,
    event_age: raw.event_age,
    genres: Array.isArray(raw.genres) ? raw.genres : [],
    medias: Array.isArray(raw.medias) ? raw.medias : [],
    // Draft events return tickets under `tickets_draft`; published events
    // use `tickets`. Normalise into the canonical `tickets` field so the
    // rest of the app can stay source-agnostic.
    tickets: Array.isArray(raw.tickets_draft)
      ? raw.tickets_draft
      : Array.isArray(raw.tickets)
        ? raw.tickets
        : [],
    tickets_draft: Array.isArray(raw.tickets_draft) ? raw.tickets_draft : undefined,
    collaborators: Array.isArray(raw.collaborators) ? raw.collaborators : [],
    project_collaborators: Array.isArray(raw.project_collaborators)
      ? raw.project_collaborators
      : [],
    event_creator_profile: raw.event_creator_profile ?? raw.creator_profile ?? null,
    event_creator_details:
      raw.event_creator_details ?? raw.event_creator_profile ?? raw.creator_profile ?? null,
    host: raw.host ?? undefined,
    attendance: raw.attendance ?? undefined,
    created_at: raw.created_at,
    featured_image: raw.featured_image ?? null,
    dominant_color: raw.dominant_color ?? null,
    interested: raw.interested ?? undefined,
    interested_count: raw.interested_count ?? undefined,
  };
}

/** Returns the primary display image URL for an event (first media in the array) */
export function getEventCoverImage(event: EventItem): string | null {
  // Profile-embedded events return featured_image instead of medias[]
  if (event.featured_image?.file_url) return event.featured_image.file_url;
  const medias = event.medias ?? [];
  for (const m of medias) {
    if (typeof m === 'string') return m;
    const url = (m as any).file_url ?? (m as any).image ?? (m as any).file ?? null;
    if (url) return url;
  }
  return null;
}

/** Returns a human-readable location string */
export function getEventLocation(event: EventItem): string | null {
  if (event.venue) return event.venue;
  if (event.address) return event.address;
  const parts = [event.city, event.state, event.country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Whether an event has ended already */
export function isEventExpired(event: EventItem, now = new Date()): boolean {
  if (!event.end) return false;
  const endDate = new Date(event.end);
  if (Number.isNaN(endDate.getTime())) return false;
  // Event times are stored "as picked" (UTC-stamped), so compare against the
  // current wall-clock time in the event's own timezone, not the real instant.
  return nowAsWallClockMs(event.timezone, now) > endDate.getTime();
}

/** Whether an event is cancelled */
export function isEventCancelled(event: EventItem): boolean {
  return Boolean(event.is_cancelled);
}

/** Whether an event is currently active (not expired and not cancelled) */
export function isEventActive(event: EventItem, now = new Date()): boolean {
  return !isEventExpired(event, now) && !isEventCancelled(event);
}

/** Human readable structure label */
export function getEventStructureLabel(event: EventItem): string | null {
  if (event.event_structure_type === 'multi_event') return 'Multi Event';
  if (event.event_structure_type === 'single_event' || event.event_structure_type === 'single') {
    return 'Single Event';
  }
  return null;
}

/**
 * Ticket price/type label for event cards — mirrors mobile SingleEventGrid._buildPriceLabel.
 * Returns null when there is nothing meaningful to show.
 */
export function getTicketLabel(
  event: EventItem,
): { text: string; variant: 'free' | 'white' } | null {
  if (event.is_private) return { text: 'Private', variant: 'white' };
  const tickets = (event.tickets ?? []).filter((t) => !t.is_archived);
  if (tickets.length === 0) {
    if (event.event_ticketing_type === 'external' || event.external_ticketing_url) {
      const raw = event.event_ticketing_type ?? 'External';
      return { text: raw.charAt(0).toUpperCase() + raw.slice(1), variant: 'white' };
    }
    return null;
  }
  const allFree = tickets.every((t) => t.price === 0);
  if (allFree) return { text: 'FREE', variant: 'free' };
  const prices = tickets.map((t) => t.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { text: min === max ? `$${min}` : `$${min} – $${max}`, variant: 'white' };
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export interface ProfileEventsResponse {
  results: EventItem[];
  count: number;
  next: string | null;
  previous: string | null;
}

/**
 * Events for a user's profile tab (paginated).
 *
 * GET /profiles/events/{userId}/?page={page}&page_size={pageSize}&active_only={activeOnly}
 */
export const useGetProfileEvents = (
  userId?: string | number,
  page = 1,
  pageSize = 8,
  activeOnly?: boolean,
) => {
  return useQuery({
    queryKey: ['events', 'profile', userId, page, pageSize, activeOnly],
    enabled: !!userId,
    queryFn: async (): Promise<ProfileEventsResponse> => {
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize),
        });
        if (activeOnly) params.set('active_only', 'true');
        const { data } = await apiClient.get(`/profiles/events/${userId}/?${params}`);
        const results = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];
        return {
          results: results.map(normaliseEvent),
          count: data?.count ?? results.length,
          next: data?.next ?? null,
          previous: data?.previous ?? null,
        };
      } catch (error) {
        console.error('[useGetProfileEvents] Failed:', error);
        return { results: [], count: 0, next: null, previous: null };
      }
    },
  });
};

export type EventFilterName =
  | 'all_events'
  | 'recent'
  | 'cancelled'
  | 'collaborators'
  | 'likes'
  | 'event_creator'
  | 'ticket_bought'
  | 'draft'
  | 'multi_event_draft';

export interface CategoryEventsResponse {
  results: EventItem[];
  count: number;
  next: string | null;
  previous: string | null;
}

/**
 * Paginated events for a single category.
 * GET /event/all_events_by_category/{filterName}/?page={page}&page_size={pageSize}
 */
export const useGetEventsByCategory = (filterName: string, page = 1, pageSize = 12) => {
  return useQuery({
    queryKey: ['events', 'category', filterName, page, pageSize],
    enabled: !!filterName,
    queryFn: async (): Promise<CategoryEventsResponse> => {
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize),
        });
        const { data } = await apiClient.get(
          `/event/all_events_by_category/${filterName}/?${params}`,
        );
        const results = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];
        return {
          results: results.map(normaliseEvent),
          count: data?.count ?? results.length,
          next: data?.next ?? null,
          previous: data?.previous ?? null,
        };
      } catch (error) {
        console.error('[useGetEventsByCategory] Failed:', error);
        return { results: [], count: 0, next: null, previous: null };
      }
    },
  });
};

/**
 * All categorised event lists for the events list page.
 * GET /event/all_events/
 */
export const useGetAllEvents = (options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['events', 'all'],
    ...options,
    queryFn: async (): Promise<RefinedEventResponse> => {
      try {
        const { data } = await apiClient.get('/event/all_events/');
        const parse = (arr: any) => (Array.isArray(arr) ? arr.map(normaliseEvent) : []);
        return {
          recommended_events: parse(data?.recommended_events),
          recent: parse(data?.recent),
          my_created_events: parse(data?.my_created_events),
          collaborators: parse(data?.collaborators),
          ticket_bought: parse(data?.ticket_bought),
          cancelled: parse(data?.cancelled),
          event_draft: parse(data?.event_draft),
          draft_single_events: parse(
            data?.draft_single_events?.length ? data.draft_single_events : data?.event_draft,
          ),
          draft_multi_events: parse(data?.draft_multi_events),
        };
      } catch (error) {
        console.error('[useGetAllEvents] Failed:', error);
        return {
          recommended_events: [],
          recent: [],
          my_created_events: [],
          collaborators: [],
          ticket_bought: [],
          cancelled: [],
          event_draft: [],
          draft_single_events: [],
          draft_multi_events: [],
        };
      }
    },
  });
};

// ─── Public Events ────────────────────────────────────────────────────────────

/** Shape of each item returned by GET /public/events/ */
export interface PublicEventItem {
  id: number;
  event_title?: string;
  start?: string;
  end?: string;
  city?: string;
  state?: string;
  country?: string;
  venue?: string;
  /** Primary event image */
  featured_image?: { file_url?: string; image?: string } | null;
  has_free_tickets?: boolean;
  has_price_range?: boolean;
  min_price?: number;
  max_price?: number;
  event_uuid?: string;
  slug?: string;
  is_cancelled?: boolean;
  event_ticketing_type?: string;
  external_ticketing_url?: string;
}

/**
 * Price/free label for a public event item.
 * - has_free_tickets → "Free"
 * - has_price_range  → "min – max"
 * - else             → "min_price"
 */
export function getPublicEventPriceLabel(
  event: PublicEventItem,
): { text: string; variant: 'free' | 'white' } | null {
  if (event.has_free_tickets) return { text: 'Free', variant: 'free' };
  if (event.has_price_range && event.min_price != null && event.max_price != null) {
    return { text: `$${event.min_price} – $${event.max_price}`, variant: 'white' };
  }
  if (event.min_price != null) return { text: `$${event.min_price}`, variant: 'white' };
  return null;
}

export interface PublicEventsPageResponse {
  events: PublicEventItem[];
  count: number;
  next: string | null;
  previous: string | null;
}

/**
 * Public event listing — no auth required.
 * GET /public/events/?page={page}&page_size={pageSize}
 *
 * Response shape: { count, next, previous, results: { events: [...] } }
 */
export const useGetPublicEvents = (page = 1, pageSize = 10, options: any = {}) => {
  return useQuery<PublicEventsPageResponse>({
    queryKey: ['events', 'public', page, pageSize],
    staleTime: 1000 * 60 * 5, // 5 min
    ...options,
    queryFn: async (): Promise<PublicEventsPageResponse> => {
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize),
        });
        const { data } = await apiClient.get(`/public/events/?${params}`);
        const events: PublicEventItem[] = Array.isArray(data?.results?.events)
          ? data.results.events
          : Array.isArray(data?.events)
            ? data.events
            : Array.isArray(data?.results)
              ? data.results
              : [];
        // Sort ascending by start date (upcoming first)
        events.sort((a, b) => {
          if (!a.start) return 1;
          if (!b.start) return -1;
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        });
        return {
          events,
          count: data?.count ?? events.length,
          next: data?.next ?? null,
          previous: data?.previous ?? null,
        };
      } catch (error) {
        console.error('[useGetPublicEvents] Failed:', error);
        return { events: [], count: 0, next: null, previous: null };
      }
    },
  });
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Single event full detail.
 *
 * When `isDraft` is known, routes directly to the correct endpoint.
 * When unknown, tries the draft endpoint first — avoiding clashes when
 * a draft and a published event share the same ID in different tables.
 */
export const useGetEventDetail = (eventId?: string | number, isDraft?: boolean) => {
  return useQuery({
    queryKey: ['events', 'detail', eventId],
    enabled: !!eventId,
    queryFn: async (): Promise<EventItem> => {
      if (isDraft) {
        const { data } = await apiClient.get(`/event/individual_event/${eventId}/draft/`);
        const raw = data?.all ?? data?.event ?? data?.data ?? data?.results ?? data;
        if (raw?.id) return normaliseEvent(raw);
        throw new Error(`Draft event ${eventId} not found`);
      }

      if (isDraft === false) {
        const { data } = await apiClient.get(`/event/individual_event/${eventId}/event/`);
        const raw = data?.event ?? data?.data ?? data?.results ?? data;
        if (raw?.id) return normaliseEvent(raw);
        throw new Error(`Event ${eventId} not found`);
      }

      // Try draft first to avoid ID clashes with published events
      try {
        const { data } = await apiClient.get(`/event/individual_event/${eventId}/draft/`);
        const raw = data?.all ?? data?.event ?? data?.data ?? data?.results ?? data;
        if (raw?.id && raw?.is_draft_or_published === 'draft') return normaliseEvent(raw);
      } catch {
        // draft endpoint failed — try published endpoint
      }

      try {
        const { data } = await apiClient.get(`/event/individual_event/${eventId}/event/`);
        const raw = data?.event ?? data?.data ?? data?.results ?? data;
        if (raw?.id) return normaliseEvent(raw);
      } catch {
        // published endpoint failed too
      }

      // Last resort: try draft endpoint again (e.g. published was 404)
      try {
        const { data } = await apiClient.get(`/event/individual_event/${eventId}/draft/`);
        const raw = data?.all ?? data?.event ?? data?.data ?? data?.results ?? data;
        if (raw?.id) return normaliseEvent(raw);
      } catch {
        // all endpoints failed
      }

      throw new Error(`Event ${eventId} not found`);
    },
  });
};

/**
 * Single event draft detail — fetches the full draft payload for editing.
 * GET /event/individual_event/{eventId}/draft/
 *
 * Mirrors mobile's GET /event/individual_event/{id}/draft/
 */
export const useGetEventDraft = (eventId?: string | number, enabled = true) => {
  return useQuery({
    queryKey: ['events', 'draft', eventId],
    enabled: !!eventId && enabled,
    queryFn: async (): Promise<EventItem> => {
      const { data } = await apiClient.get(`/event/individual_event/${eventId}/draft/`);
      const raw = data?.all ?? data?.event ?? data?.data ?? data?.results ?? data;
      if (raw?.id) return normaliseEvent(raw);
      throw new Error(`Draft event ${eventId} not found`);
    },
  });
};

/**
 * Single public event detail — no auth required.
 * GET /public/events/{eventUuid}/
 */
export const useGetPublicEventDetail = (eventUuid?: string | number) => {
  return useQuery({
    queryKey: ['events', 'public', 'detail', eventUuid],
    enabled: !!eventUuid,
    queryFn: async (): Promise<EventItem> => {
      const { data } = await apiClient.get(`/public/events/${eventUuid}/`);
      // Response: { success: true, event: { ... } }
      const raw = data?.event ?? data?.data ?? data?.results ?? data;
      return normaliseEvent(raw);
    },
  });
};

// ─── Check-In ─────────────────────────────────────────────────────────────────

export interface CheckInResponse {
  success?: boolean;
  message?: string;
  error?: string;
  ticket_type?: string;
  ticket_instance_id?: number;
  verification_uuid?: string;
  verification_code?: string;
  ticket_type_name?: string;
  checked_into_event?: {
    id: number;
    title: string;
    date: string;
  };
  remaining_events?: unknown[];
  check_in_time?: string;
  qr_code_refreshed?: boolean;
  remaining_check_ins?: number;
  // Legacy fields
  checked_in_at?: string;
}

/**
 * Check in a guest by check-in / verification code.
 * POST /event/online_check_in/{eventId}/
 */
export async function checkInTicket(
  eventId: string | number,
  verificationCode: string,
): Promise<CheckInResponse> {
  const { data } = await apiClient.post<CheckInResponse>(`/event/online_check_in/${eventId}/`, {
    verification_code: verificationCode,
  });
  return data;
}

// ─── Sub-Events ───────────────────────────────────────────────────────────────

/**
 * Sub-events for a multi_event parent.
 * GET /event/multi/main-events/{eventId}/sub-events/
 */
export const useGetSubEvents = (eventId?: string | number, enabled = false) => {
  return useQuery<EventItem[]>({
    queryKey: ['events', 'sub-events', eventId],
    enabled: !!eventId && enabled,
    queryFn: async (): Promise<EventItem[]> => {
      const { data } = await apiClient.get(`/event/multi/main-events/${eventId}/sub-events/`);
      const raw: any[] = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.sub_events)
          ? data.sub_events
          : Array.isArray(data)
            ? data
            : [];
      return raw.map(normaliseEvent);
    },
  });
};

// ─── Create / Update Event Types & Mutations ───────────────────────────────────

export interface MediaUrlEntry {
  public_id: string;
  secure_url: string;
  resource_type: string;
  bytes?: number;
  format?: string;
}

export interface MediaInstruction {
  id: number;
  delete: boolean;
}

export interface EventTicketRequest {
  id?: number;
  ticket_type: string;
  price: number;
  ticket_number_issued?: number;
  remaining_tickets?: number;
  description?: string;
  start_at?: string;
  expired_at?: string;
  set_purchase_date?: boolean;
  is_multi_event_pass?: boolean;
  access_type?: string;
  accessible_sub_event_ids?: number[];
  use_publish_time_for_start_at?: boolean;
  delete?: boolean;
}

export interface SingleEventCreationPayload {
  event_title: string;
  event_type: string;
  event_age: string;
  info: string;
  location_body: {
    street: string;
    city: string;
    state: string;
    country: string;
    longitude: number;
    latitude: number;
  };
  genres: number[];
  start: string;
  end: string;
  sponsors: string;
  email: string;
  address: string;
  venue?: string;
  collaborators?: number[];
  draft_id?: number;
  is_private?: boolean;
  guest_emails?: string[];
  guest_usernames?: string[];
  promo_codes?: Array<Record<string, unknown>>;
  iana_timezone?: string;
  /** Dominant color of the first/primary event image, as a "#rrggbb" hex string. */
  dominant_color?: string | null;
}

export interface ExternalEventCreationPayload {
  event_title: string;
  /** "physical" | "virtual" — required by the backend serializer even for external events */
  event_type?: string;
  location_body: {
    street: string;
    city: string;
    state: string;
    country: string;
    longitude: number;
    latitude: number;
  };
  start: string;
  end: string;
  address: string;
  venue?: string;
  collaborators?: number[];
  external_ticketing_url: string;
  event_ticketing_type: 'external';
  iana_timezone?: string;
  /** When promoting a draft to published, the ID of the original draft. */
  draft_id?: number;
  info?: string;
  /** Dominant color of the first/primary event image, as a "#rrggbb" hex string. */
  dominant_color?: string | null;
}

type EventDraftPayload =
  | {
      event: SingleEventCreationPayload;
      ticket: EventTicketRequest[];
      media_urls?: MediaUrlEntry[];
      media_instructions?: MediaInstruction[];
    }
  | {
      event: ExternalEventCreationPayload;
      media_urls?: MediaUrlEntry[];
      media_instructions?: MediaInstruction[];
    };

/**
 * Diff-based payload for patching a draft event.
 * Only the fields that actually changed are included — omitting unchanged
 * dates, tickets, and other fields keeps the request minimal.
 */
export interface DraftUpdatePayload {
  event: Record<string, unknown>;
  ticket?: EventTicketRequest[];
  media_urls?: MediaUrlEntry[];
  media_instructions?: MediaInstruction[];
}

export interface CreatedEventResponse {
  message?: string;
  event: EventItem;
  medias?: EventMedia[];
  event_id?: number;
  event_uuid?: string;
  /** Project auto-created alongside the event */
  project?: { project_uuid?: string; status?: string; [key: string]: unknown } | null;
}

/**
 * Create a draft event (single or external).
 * POST /event/create_event_draft/
 *
 * JSON payload structure mirrors Flutter:
 *   { event: {...}, ticket: [...], media_urls: [...], media_instructions: [...] }
 * Ticket key is omitted for external events.
 */
export const useCreateEventDraft = () => {
  return useMutation<CreatedEventResponse, Error, EventDraftPayload>({
    mutationFn: async (payload): Promise<CreatedEventResponse> => {
      const { data } = await apiClient.post('/event/create_event_draft/', payload);
      const eventData = data?.event ?? data;
      return {
        message: data?.message,
        event: normaliseEvent(eventData),
        medias: data?.medias,
        event_id: data?.event_id ?? eventData?.id,
        event_uuid: data?.event_uuid ?? eventData?.event_uuid,
        project: data?.project ?? eventData?.project ?? null,
      };
    },
  });
};

/**
 * Create / publish an event directly (single or external).
 * POST /event/create_event/
 *
 * Uses multipart/form-data to match Flutter's FormData structure:
 *   event: JSON-stringified event object
 *   ticket: JSON-stringified tickets array (single events only)
 *   media_urls: JSON-stringified Cloudinary URLs (optional)
 *   media_instructions: JSON-stringified deletion instructions (optional)
 */
export const useCreateEvent = () => {
  return useMutation<CreatedEventResponse, Error, EventDraftPayload>({
    mutationFn: async (payload): Promise<CreatedEventResponse> => {
      const form = new FormData();
      form.append('event', JSON.stringify(payload.event));

      if ('ticket' in payload && payload.ticket) {
        form.append('ticket', JSON.stringify(payload.ticket));
      }
      if (payload.media_urls && payload.media_urls.length > 0) {
        form.append('media_urls', JSON.stringify(payload.media_urls));
      }
      if (payload.media_instructions && payload.media_instructions.length > 0) {
        form.append('media_instructions', JSON.stringify(payload.media_instructions));
      }

      const { data } = await apiClient.post('/event/create_event/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const eventData = data?.event ?? data;
      return {
        message: data?.message,
        event: normaliseEvent(eventData),
        medias: data?.medias,
        event_id: data?.event_id ?? eventData?.id,
        event_uuid: data?.event_uuid ?? eventData?.event_uuid,
        project: data?.project ?? eventData?.project ?? null,
      };
    },
  });
};

/**
 * Update a draft event (single or external).
 * PATCH /event/update_event_draft/{eventId}/
 *
 * Accepts either a full EventDraftPayload (used when publishing) or a
 * diff-only DraftUpdatePayload (used when saving — only changed fields sent).
 */
export const useUpdateEventDraft = () => {
  return useMutation<
    CreatedEventResponse,
    Error,
    { eventId: number; payload: EventDraftPayload | DraftUpdatePayload }
  >({
    mutationFn: async ({ eventId, payload }): Promise<CreatedEventResponse> => {
      const { data } = await apiClient.patch(`/event/update_event_draft/${eventId}/`, payload);
      const eventData = data?.event ?? data;
      return {
        message: data?.message,
        event: normaliseEvent(eventData),
        medias: data?.medias,
        event_id: data?.event_id ?? eventData?.id,
        event_uuid: data?.event_uuid ?? eventData?.event_uuid,
        project: data?.project ?? eventData?.project ?? null,
      };
    },
  });
};

/**
 * Diff-based payload for editing a published event.
 * Mirrors Flutter's minimal-payload approach:
 *   event: JSON-stringified diff object containing event fields + tickets + collaborators + promo_codes
 *   media_urls: JSON-stringified array of newly uploaded Cloudinary URLs (only new uploads)
 *   media_instructions: JSON-stringified array of deletions [{ id, delete: true }]
 *   images: File objects for new image files
 */
export interface PublishedEventEditPayload {
  event: Record<string, unknown>;
  media_urls?: MediaUrlEntry[];
  media_instructions?: MediaInstruction[];
  images?: File[];
}

/**
 * Edit a published event (single or external).
 * PUT /event/multi/events/{eventId}/edit/
 *
 * Sends a diff-based payload via multipart/form-data — only changed fields are sent.
 * Tickets are nested inside the "event" JSON key, NOT sent as a separate FormData key.
 * Media: only newly uploaded images (media_urls) and deletion instructions (media_instructions).
 */
export const useEditPublishedEvent = () => {
  return useMutation<
    CreatedEventResponse,
    Error,
    { eventId: number; payload: PublishedEventEditPayload }
  >({
    mutationFn: async ({ eventId, payload }): Promise<CreatedEventResponse> => {
      const form = new FormData();
      form.append('event', JSON.stringify(payload.event));

      if (payload.media_urls && payload.media_urls.length > 0) {
        form.append('media_urls', JSON.stringify(payload.media_urls));
      }
      if (payload.media_instructions && payload.media_instructions.length > 0) {
        form.append('media_instructions', JSON.stringify(payload.media_instructions));
      }
      if (payload.images && payload.images.length > 0) {
        payload.images.forEach((file) => form.append('images', file));
      }

      const { data } = await apiClient.put(`/event/multi/events/${eventId}/edit/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const eventData = data?.event ?? data;
      return {
        message: data?.message,
        event: normaliseEvent(eventData),
        medias: data?.medias,
        event_id: data?.event_id ?? eventData?.id,
        event_uuid: data?.event_uuid ?? eventData?.event_uuid,
        project: data?.project ?? eventData?.project ?? null,
      };
    },
  });
};

// ─── Cancel Event ─────────────────────────────────────────────────────────────

// ─── Stripe Account ──────────────────────────────────────────────────────────

export interface StripeAccountDetails {
  is_available: boolean;
  on_boarding_completed: boolean;
  country: string;
  can_receive_payments: boolean;
}

/**
 * Check Stripe account status.
 * GET /profiles/stripe-account-detail/
 */
export const useGetStripeAccountStatus = (options: { enabled?: boolean } = {}) => {
  return useQuery<StripeAccountDetails>({
    queryKey: ['stripe', 'account'],
    ...options,
    queryFn: async (): Promise<StripeAccountDetails> => {
      const { data } = await apiClient.get('/profiles/stripe-account-detail/');
      return {
        is_available: data?.is_available ?? false,
        on_boarding_completed: data?.on_boarding_completed ?? false,
        country: data?.country ?? '',
        can_receive_payments: data?.can_receive_payments ?? false,
      };
    },
  });
};

// ─── Username Check ──────────────────────────────────────────────────────────

/**
 * Check if a username exists on the platform.
 * The event form uses this to validate @username invites for private events.
 *
 * GET /custom_account/username-availability/?username={username}
 * available === false → username IS taken (user exists)
 * available === true  → username is free (user does not exist)
 */
export const useCheckUsernameAvailability = () => {
  return useMutation<{ available: boolean; message?: string }, Error, string>({
    mutationFn: async (username: string) => {
      const encoded = encodeURIComponent(username);
      const { data } = await apiClient.get(
        `/custom_account/username-availability/?username=${encoded}`,
      );
      return {
        available: data?.available ?? true,
        message: data?.message,
      };
    },
  });
};

export interface CancelEventResponse {
  success?: boolean;
  message?: string;
}

/**
 * Cancel an event.
 * POST /event/cancel_event/{event_id}/
 */
export const useCancelEvent = () => {
  return useMutation<CancelEventResponse, Error, number>({
    mutationFn: async (eventId: number): Promise<CancelEventResponse> => {
      const { data } = await apiClient.post(`/event/cancel_event/${eventId}/`);
      return data;
    },
  });
};

export interface DeleteEventDraftPayload {
  draftId: number;
  draftType: 'single-draft' | 'multi-draft' | 'sub-draft';
}

/**
 * Permanently delete a draft event.
 * DELETE /event/drafts/{draft_id}/{draft_type}/delete/
 */
export const useDeleteEventDraft = () => {
  return useMutation<void, Error, DeleteEventDraftPayload>({
    mutationFn: async ({ draftId, draftType }: DeleteEventDraftPayload): Promise<void> => {
      await apiClient.delete(`/event/drafts/${draftId}/${draftType}/delete/`);
    },
  });
};

// ─── Private Event Invite Management ─────────────────────────────────────────

export interface EventInvite {
  id: number;
  email: string;
  status: 'active' | 'purchased' | 'revoked';
  invite_id?: number;
  is_automatic?: boolean;
  automatic?: boolean;
}

export interface EventInviteLinkData {
  link: string;
  are_links_active?: boolean;
}

/**
 * List all invites for a private event.
 * GET /event/events/{eventId}/invites/
 */
export const useListEventInvites = (eventId: string | number) => {
  return useQuery<EventInvite[], Error>({
    queryKey: ['event-invites', eventId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/event/events/${eventId}/invites/`);
      return (data?.results ?? []) as EventInvite[];
    },
    enabled: !!eventId,
  });
};

/**
 * Generate / fetch invite link for a private event.
 * GET /event/events/{eventId}/invites/get-link/
 */
export const useGetEventInviteLink = (eventId: string | number) => {
  return useQuery<EventInviteLinkData, Error>({
    queryKey: ['event-invite-link', eventId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/event/events/${eventId}/invites/get-link/`);
      return (data?.data ?? { link: '' }) as EventInviteLinkData;
    },
    enabled: !!eventId,
  });
};

/**
 * Send invites to a private event.
 * POST /event/events/{eventId}/invites/send/
 *
 * Uses multipart/form-data with JSON-stringified arrays.
 */
export const useSendEventInvites = (eventId: string | number) => {
  return useMutation<
    { success?: boolean; message?: string },
    Error,
    { guest_emails: string[]; guest_usernames: string[] }
  >({
    mutationFn: async (payload) => {
      const formData = new FormData();
      if (payload.guest_emails.length > 0) {
        formData.append('emails', JSON.stringify(payload.guest_emails));
      }
      if (payload.guest_usernames.length > 0) {
        formData.append('usernames', JSON.stringify(payload.guest_usernames));
      }
      const { data } = await apiClient.post(`/event/events/${eventId}/invites/send/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
};

/**
 * Resend an individual event invite.
 * POST /event/events/invites/{inviteId}/resend/
 */
export const useResendEventInvite = () => {
  return useMutation<{ success?: boolean; message?: string }, Error, number>({
    mutationFn: async (inviteId: number) => {
      const formData = new FormData();
      const { data } = await apiClient.post(`/event/events/invites/${inviteId}/resend/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
};

/**
 * Revoke an individual event invite.
 * POST /event/events/invites/{inviteId}/revoke/
 */
export const useRevokeEventInvite = () => {
  return useMutation<{ success?: boolean; message?: string }, Error, number>({
    mutationFn: async (inviteId: number) => {
      const formData = new FormData();
      const { data } = await apiClient.post(`/event/events/invites/${inviteId}/revoke/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
};

// ─── Collaborator Management ─────────────────────────────────────────────────

export interface CollaboratorData {
  id: number;
  user_id: number;
  invitee_email?: string | null;
  invitee_name?: string | null;
  status: string;
  invited_at?: string;
  updated_at?: string;
  event_title?: string;
}

export interface CollaboratorInvitation {
  id: number;
  invitee_email: string;
  status: string;
  invited_at: string;
  user_id?: number;
}

/** Standardised `event_type` values accepted by the collaborator endpoints. */
export type CollaboratorEventType = 'single_event_draft' | 'multi_event_draft' | 'published_event';

/**
 * Derives the collaborator `event_type` from an event's draft/published and
 * single/multi-event state.
 */
export function getCollaboratorEventType(
  event: Pick<EventItem, 'is_draft_or_published' | 'event_structure_type' | 'is_sub_event'>,
): CollaboratorEventType {
  if (event.is_draft_or_published !== 'draft') return 'published_event';
  const isMultiEvent = event.event_structure_type === 'multi_event' && !event.is_sub_event;
  return isMultiEvent ? 'multi_event_draft' : 'single_event_draft';
}

/**
 * Fetch existing collaborators for an event — the collaborator invitations
 * endpoint is the source of truth for this list (covers both accepted and
 * pending collaborators).
 * GET /event/{eventId}/collaborators/invitations/?event_type=<event_type>
 */
export const useListEventCollaborators = (
  eventId: string | number,
  eventType: CollaboratorEventType,
) => {
  return useQuery<CollaboratorData[], Error>({
    queryKey: ['event-collaborators', eventId, eventType],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/event/${eventId}/collaborators/invitations/?event_type=${eventType}`,
      );
      return (data?.data ?? []) as CollaboratorData[];
    },
    enabled: !!eventId,
  });
};

/**
 * Fetch pending collaborator email invitations.
 * GET /event/{eventId}/collaborators/invitations/?event_type=<event_type>
 */
export const useListCollaboratorInvitations = (
  eventId: string | number,
  eventType: CollaboratorEventType,
) => {
  return useQuery<CollaboratorInvitation[], Error>({
    queryKey: ['event-collaborator-invitations', eventId, eventType],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/event/${eventId}/collaborators/invitations/?event_type=${eventType}`,
      );
      return (data?.data ?? data ?? []) as CollaboratorInvitation[];
    },
    enabled: !!eventId,
  });
};

/**
 * Resend a pending collaborator invitation.
 * POST /event/invitations/{invitationId}/resend/
 */
export const useResendCollaboratorInvitation = () => {
  return useMutation<{ success?: boolean; message?: string }, Error, number>({
    mutationFn: async (invitationId: number) => {
      const { data } = await apiClient.post(`/event/invitations/${invitationId}/resend/`);
      return data;
    },
  });
};

/**
 * Add collaborators to an event.
 * POST /event/{eventId}/collaborators/add/?event_type=<event_type>
 */
export const useAddEventCollaborators = (eventId: string | number) => {
  return useMutation<
    {
      results?: {
        successful?: Array<{ user_id: number; username: string }>;
        failed?: unknown[];
        not_found?: number[];
      };
    },
    Error,
    { user_ids: number[]; event_type: CollaboratorEventType; message?: string }
  >({
    mutationFn: async (payload) => {
      const body: Record<string, unknown> = {
        user_ids: payload.user_ids,
      };
      if (payload.message) body.message = payload.message;
      const { data } = await apiClient.post(
        `/event/${eventId}/collaborators/add/?event_type=${payload.event_type}`,
        body,
      );
      return data;
    },
  });
};

/**
 * Remove a collaborator from an event.
 * POST /event/{eventId}/collaborators/remove/?event_type=<event_type>
 */
export const useRemoveEventCollaborator = (eventId: string | number) => {
  return useMutation<
    { success?: boolean; message?: string },
    Error,
    { user_id: number; event_type: CollaboratorEventType }
  >({
    mutationFn: async (payload) => {
      const body = {
        user_id: payload.user_id,
      };
      const { data } = await apiClient.post(
        `/event/${eventId}/collaborators/remove/?event_type=${payload.event_type}`,
        body,
      );
      return data;
    },
  });
};

// ─── Event interest ("Not ready to buy yet?") ────────────────────────────────

export interface EventInterestPayload {
  full_name?: string;
  email?: string;
  recaptcha_token?: string;
}

/**
 * Mark interest in an event.
 * POST /event/{event_id}/interests/ — name/email are required for guests,
 * optional when authenticated.
 */
export const useCreateEventInterest = (eventId: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, EventInterestPayload>({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post(`/event/${eventId}/interests/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', 'detail'] });
    },
  });
};

// ─── External event ticket click / purchase response ─────────────────────────

export type TicketPurchaseStatus = 'purchased' | 'not_purchased';

export interface TicketClickPayload {
  full_name?: string;
  email?: string;
}

export interface TicketClickResult {
  url: string;
  response_id: string;
}

/** POST /event/<id>/ticket-click/ — records the click and returns the URL to redirect to. */
export const useExternalTicketClick = () =>
  useMutation<TicketClickResult, Error, { eventId: number | string; payload: TicketClickPayload }>({
    mutationFn: async ({ eventId, payload }) => {
      const { data } = await apiClient.post(`/event/${eventId}/ticket-click/`, payload);
      return data.data as TicketClickResult;
    },
  });

/** PATCH /event/<id>/ticket-responses/<response_id>/ */
export const useUpdateTicketPurchaseStatus = () =>
  useMutation<
    { response_id: string; purchase_status: TicketPurchaseStatus },
    Error,
    { eventId: number | string; responseId: string; status: TicketPurchaseStatus }
  >({
    mutationFn: async ({ eventId, responseId, status }) => {
      const { data } = await apiClient.patch(`/event/${eventId}/ticket-responses/${responseId}/`, {
        purchase_status: status,
      });
      return data.data;
    },
  });
